import axios from "axios";
import {
  applyViewConfiguration,
  setPinnedColumns,
  setColumnOrder,
  setFilterModel,
  setSortModel,
} from "../../slices/dataTable/data";
import { API } from "../../../config";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { RootState } from "../../../configureStore";
import { setDateRangePicker } from "../../slices/dataTable/dataTableFilters";
import { dataTableActionNewFilter } from "./DataTableAsyncActions";
import {
  setAppliedViewName,
  setRunAppliedView,
  setTableConfiguration,
} from "../../slices/dataTable/dataViews";
import { SAVE_DATA_VIEW_CONSTANT } from "../../../constants/save-views";
import { setTableDataOptions } from "../../slices/UnifiedSlices/SharedDataReducer";

export const getDataViews = createAsyncThunk(
  "DataViewsReducer/getDataViews",
  async (_, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;

    const token = user?.token || "";

    const headers = {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    };
    try {
      const response = await axios.get(
        `${API}/tools/saved-config?&typeConstantId=${SAVE_DATA_VIEW_CONSTANT}`,
        { headers }
      );
      const { data } = response;

      return data;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  }
);

export const createNewView = createAsyncThunk(
  "DataViewsReducer/createNewView",
  async (_, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;
    const { tableConfiguration } = (store.getState() as RootState)
      .DataViewsReducer;

    const token = user?.token || "";

    const headers = {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await axios.post(
        `${API}/tools/saved-config`,
        {
          name: tableConfiguration.name,
          description: tableConfiguration.description,
          typeConstantId: SAVE_DATA_VIEW_CONSTANT,
          config: tableConfiguration.config,
        },
        { headers }
      );

      const { data } = response;

      return data;
    } catch (error) {
      return store.rejectWithValue(error);
    }
  }
);

export const deleteView = createAsyncThunk(
  "DataViewsReducer/deleteView",
  async (viewId: string, store) => {
    const { locale } = (store.getState() as RootState).LayoutReducer;
    const { user } = (store.getState() as RootState).Authentication;

    const token = user?.token || "";

    const headers = {
      "Accept-Language": `${locale}`,
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await axios.delete(
        `${API}/tools/saved-config/${viewId}`,
        { headers }
      );

      const { data } = response;

      if (data.id === viewId) {
        const updatedViews = await store.dispatch(getDataViews());

        if (updatedViews.type.endsWith("/rejected")) {
          throw new Error("Failed to refresh views after deletion");
        }

        const newDataViewsObject =
          updatedViews.payload?.data?.map((views: any) => ({
            id: views.id,
            name: views.name,
            savedDate: new Date(views.createdAt).toLocaleDateString(),
            filters: views.config.filters,
            configuration: views.config.table,
            createdAt: views.createdAt,
            dateFilter: views.config.date || null,
          })) || [];

        return {
          deletedView: data,
          updatedViews: newDataViewsObject,
          success: true,
          message: "View deleted successfully",
        };
      } else {
        throw new Error("View deletion confirmation failed");
      }
    } catch (error: any) {
      return store.rejectWithValue({
        error: error.message || "Error deleting view",
        success: false,
      });
    }
  }
);

export const applyDataTableView = createAsyncThunk(
  "applyDataTableView",
  async (viewConfig: any, store) => {
    const { dispatch, rejectWithValue } = store;

    try {
      if (!viewConfig) {
        return { success: false, message: "No view configuration provided" };
      }

      const configuration = viewConfig.configuration || {};
      const dateFilter = viewConfig.dateFilter || viewConfig.date;
      const filters = viewConfig.filters;
      const viewName = viewConfig.name;

      dispatch(setRunAppliedView(true));
      dispatch(setAppliedViewName(viewName));
      // Apply all configuration changes first
      if (configuration.tableOptions) {
        dispatch(setTableDataOptions(configuration.tableOptions));
      }

      if (configuration.sortModel.length > 0) {
        dispatch(setSortModel(configuration.sortModel));
      }

      if (configuration.columnsVisibility) {
        dispatch(applyViewConfiguration(configuration.columnsVisibility));
      }

      if (configuration.pinnedColumns) {
        dispatch(setPinnedColumns(configuration.pinnedColumns));
      }

      if (configuration.columnOrder) {
        dispatch(setColumnOrder(configuration.columnOrder));
      }

      if (filters && filters.items && filters.items.length > 0) {
        dispatch(setFilterModel(filters));
      }

      if (
        dateFilter?.dataDateActivity &&
        Array.isArray(dateFilter.dataDateActivity)
      ) {
        const dateArray = dateFilter.dataDateActivity.map(
          (d: any) => new Date(d)
        );
        dispatch(setDateRangePicker(dateArray));
      }

      requestAnimationFrame(() => {
        dispatch(dataTableActionNewFilter());
      });

      return { success: true };
    } catch (error: any) {
      return rejectWithValue(error.message || "Error applying view");
    }
  }
);

export const saveCurrentView = createAsyncThunk(
  "dataViews/saveCurrentView",
  async (
    payload: {
      viewName: string;
      viewDescription: string;
      tableConfig: any;
      filtersConfig: any;
      dataDateActivityConfig: any;
      hasDateFilterChanged: boolean;
    },
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      const {
        viewName,
        viewDescription,
        tableConfig,
        filtersConfig,
        dataDateActivityConfig,
        hasDateFilterChanged,
      } = payload;

      if (!viewName.trim()) {
        return rejectWithValue("View name is required");
      }

      const configToSave: any = {
        version: "2.0",
        table: tableConfig,
        filters: filtersConfig,
        timestamp: new Date().toISOString(),
      };

      if (hasDateFilterChanged) {
        configToSave.date = dataDateActivityConfig;
      }

      const currentConfiguration = {
        name: viewName.trim(),
        description: viewDescription.trim(),
        config: configToSave,
      };

      // Set the table configuration in the state
      dispatch(setTableConfiguration(currentConfiguration));

      // Create the new view
      const result = await dispatch(createNewView());

      if (result.type.endsWith("/rejected")) {
        throw new Error("Create view was rejected");
      }

      const updatedViews = await dispatch(getDataViews());

      return {
        success: true,
        message: "View created successfully",
        updatedViews: updatedViews.payload,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Error saving view");
    }
  }
);
